import { CommonModule } from '@angular/common';
import { Component, inject, isDevMode, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MenuItem, MessageService, TreeNode } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { Dialog } from "primeng/dialog";
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TreeTableModule } from 'primeng/treetable';
import { ReducedEditor } from "../../components/layout/reduced-editor/reduced-editor";
import { Shorty } from '../../pipes/shorty';
import { AufgabenService } from '../../services/aufgaben-service';
import { FallaufgabenService } from '../../services/fallaufgaben-service';
import { Status } from '../../services/status';
import { TeilaufgabenService } from '../../services/teilaufgaben-service';
import { PdfAdminService, PdfDocument } from '../../services/pdf-admin.service';
import { Sessions } from '../sessions/sessions';
import { TeilaufgabeDialog } from './teilaufgabe-dialog/teilaufgabe-dialog';
import { TeilaufgabenCopyService } from './teilaufgaben-copy.service';

@Component({
  selector: 'app-fallaufgaben-details',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    ConfirmDialog,
    Message,
    MultiSelectModule,
    InputTextModule,
    TagModule,
    InputNumber,
    TreeTableModule,
    TooltipModule,
    BreadcrumbModule,
    ContextMenuModule,
    Shorty,
    Dialog,
    TabsModule,
    Sessions,
    ReducedEditor,
    TeilaufgabeDialog
  ],
  templateUrl: './fallaufgaben-details.html',
  styleUrl: './fallaufgaben-details.scss',
  providers: [ConfirmationService]
})
export class FallaufgabenDetails implements OnInit {
  @ViewChild('cm') cm!: ContextMenu;

  protected fallaufgabenService = inject(FallaufgabenService);
  protected aufgabenService = inject(AufgabenService);
  protected teilaufgabenService = inject(TeilaufgabenService);
  protected teilaufgabenCopyService = inject(TeilaufgabenCopyService);
  protected isAufgabeVisible: boolean = false;
  protected isTeilaufgabeVisible: boolean = false;
  protected maxPointsInFallaufgabe: number = 0;
  protected liveStateWarning: string | null = null;
  protected tasks: TreeNode[] = [];
  protected availableDocuments: PdfDocument[] = [];
  protected items: MenuItem[] = [{ label: this.fallaufgabenService.selectedFallaufgabe.name }];
  protected home: MenuItem = { label: 'Fallaufgaben', routerLink: '/fallaufgaben' };

  protected statusService = inject(Status);
  private messageService = inject(MessageService);
  private confirmService = inject(ConfirmationService);
  private pdfAdminService = inject(PdfAdminService);

  public ngOnInit(): void {
    this.createTreeNode();
    void this.loadAvailableDocuments();
  }

  private async loadAvailableDocuments() {
    this.availableDocuments = await this.pdfAdminService.listPdfs();
  }

  protected toggleParentNode(rowNode: any) {
    rowNode.node.expanded = !rowNode.node.expanded;
    this.tasks = [...this.tasks];
  }

  protected createTreeNode(expandedParent: string = '') {
    this.tasks = [];
    this.maxPointsInFallaufgabe = 0;
    this.aufgabenService.aufgaben.forEach((a) => {
      const taArr = this.teilaufgabenService.teilaufgaben.filter((ta) => ta.aufgabenId === a._id);
      let points: number = 0;
      const childTreeNodes: TreeNode[] = [
        ...taArr.map((ta) => {
          points += ta.maxScore;
          return {
            key: ta._id,
            data: {
              id: ta._id,
              name: ta.aufgabenstellung,
              points: ta.maxScore,
              number: a.aufgabenIndex + '.' + ta.teilaufgabenIndex,
              type: ta.type === 'standard' ? 'Teilaufgabe (Standard)' : 'Teilaufgabe (Tabelle)'
            }
          }
        })
      ];
      childTreeNodes.push({
        key: 'addButton',
        data: {
          id: a._id,
          name: '',
          points: '',
          type: "addButton"
        }
      })
      const newTreeNode: TreeNode = {
        key: a._id,
        data: {
          id: a._id,
          name: a.title,
          points,
          number: a.aufgabenIndex,
          type: "Aufgabe"
        },
        children: childTreeNodes
      }
      if (expandedParent !== '' && expandedParent === a._id) {
        newTreeNode.expanded = true;
      }
      this.tasks.push(newTreeNode)
      this.maxPointsInFallaufgabe += points;
    });
  }

  protected async startNewUserSession() {
    const ref = window.open()
    this.statusService.getNewUserLinkForFallaufgabe(this.fallaufgabenService.selectedFallaufgabe._id).then(result => {
      ref!.location = (isDevMode() ? result.replaceAll("https://pkvdev-lernbegleiter.reflact.info/", "http://localhost:4203/") : result);
    })
  }

  protected async createAufgabe() {
    const id = await this.aufgabenService.create({ einleitung: '', fallaufgabenId: this.fallaufgabenService.selectedFallaufgabe._id, aufgabenIndex: this.aufgabenService.aufgaben.length + 1, title: 'Neue Aufgabe' })
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Aufgabe wurde angelegt.`, life: 3000 });
    this.createTreeNode();
    this.openEditModal(id, 'Aufgabe');
  }

  protected async createTeilaufgabe(aufgabenId: string, type: 'standard' | 'table') {
    const count = this.teilaufgabenService.teilaufgaben.filter(ta => ta.aufgabenId === aufgabenId).length;
    const id = await this.teilaufgabenService.create({ aufgabenId, aufgabenstellung: 'Neue Teilaufgabe', teilaufgabenIndex: count + 1, type });
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Teilaufgabe wurde angelegt.`, life: 3000 });
    this.createTreeNode(aufgabenId);
    this.openEditModal(id, 'Teilaufgabe');
  }

  protected async pasteTeilaufgabe(targetAufgabenId: string) {
    const id = await this.teilaufgabenCopyService.paste(targetAufgabenId);
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Teilaufgabe wurde angelegt.`, life: 3000 });
    this.createTreeNode(targetAufgabenId);
    this.openEditModal(id, 'Teilaufgabe');

  }

  protected async confirmStateChange(event: Event, state: 'live' | 'archived') {
    this.liveStateWarning = state === 'live' ? this.getBewertungsmatrixWarning() : null;

    if (state === 'live') {
      this.confirmService.confirm({
        position: 'top',
        target: event.currentTarget as EventTarget,
        message: `Sind Sie sicher, dass Sie die Fallaufgabe '${this.fallaufgabenService.selectedFallaufgabe.name}' nun <b>live</b> schalten möchten?`,
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Abbrechen',
          severity: 'secondary',
          outlined: true
        },
        acceptButtonProps: {
          label: 'Live schalten',
          severity: 'warn',
        },
        accept: async () => {
          await this.fallaufgabenService.changeState({ _id: this.fallaufgabenService.selectedFallaufgabe._id, state });
          this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${this.fallaufgabenService.selectedFallaufgabe.name} wurde live geschaltet.`, life: 3000 });
          this.liveStateWarning = null;
        },
        reject: () => {
          this.liveStateWarning = null;
        }
      });
    } else {
      this.confirmService.confirm({
        position: 'top',
        target: event.currentTarget as EventTarget,
        message: `Sind Sie sicher, dass Sie die Fallaufgabe '${this.fallaufgabenService.selectedFallaufgabe.name}' nun <b>archivieren</b> schalten möchten?`,
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Abbrechen',
          severity: 'secondary',
          outlined: true
        },
        acceptButtonProps: {
          label: 'Archivieren',
          severity: 'danger',
        },
        accept: async () => {
          await this.fallaufgabenService.changeState({ _id: this.fallaufgabenService.selectedFallaufgabe._id, state });
          this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${this.fallaufgabenService.selectedFallaufgabe.name} wurde archiviert.`, life: 3000 });
        }
      });
    }
  }

  private getBewertungsmatrixWarning(): string | null {
    const schluessel = this.fallaufgabenService.selectedFallaufgabe.bewertung.schluessel;
    if (!schluessel.length) {
      return null;
    }

    const allZero = schluessel.every((entry) => entry.minPunkte === 0);
    if (allZero) {
      const notePointPairs = [...schluessel]
        .sort((a, b) => b.note - a.note)
        .map((entry) => `Note ${entry.note}: ${entry.minPunkte} Punkte`)
        .join(', ');
      return `In der Bewertungsmatrix stehen fuer alle Noten 0 Punkte (${notePointPairs}).`;
    }

    const sortedByGrade = [...schluessel].sort((a, b) => b.note - a.note);
    for (let i = 1; i < sortedByGrade.length; i++) {
      if (sortedByGrade[i].minPunkte <= sortedByGrade[i - 1].minPunkte) {
        const lowerGrade = sortedByGrade[i - 1];
        const higherGrade = sortedByGrade[i];
        return `Die Punkte sind von Note 6 bis Note 1 nicht aufsteigend: Note ${lowerGrade.note} hat ${lowerGrade.minPunkte} Punkte, Note ${higherGrade.note} hat ${higherGrade.minPunkte} Punkte.`;
      }
    }

    return null;
  }

  protected async confirmDelete(itemId: string, type: 'Aufgabe' | 'Teilaufgabe') {
    if (this.fallaufgabenService.selectedFallaufgabe.state === 'draft') {
      let message: string = '';
      let parentId: string = '';
      if (type === 'Aufgabe') {
        const item = this.aufgabenService.aufgaben.find(a => a._id === itemId);
        if (item) {
          message = `Sind Sie sicher, dass Sie <b>Aufgabe ${item.aufgabenIndex}</b> löschen möchten?`;
          parentId = item.fallaufgabenId;
        }
      } else {
        const item = this.teilaufgabenService.teilaufgaben.find(ta => ta._id === itemId);
        if (item) {
          const parentItem = this.aufgabenService.aufgaben.find(a => a._id === item.aufgabenId);
          message = `Sind Sie sicher, dass Sie die <b>Teilaufgabe ${parentItem?.aufgabenIndex}.${item.teilaufgabenIndex}</b> löschen möchten?`;
          parentId = item.aufgabenId;
        }
      }
      this.confirmService.confirm({
        position: 'top',
        message,
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Abbrechen',
          severity: 'secondary',
          outlined: true
        },
        acceptButtonProps: {
          label: 'Löschen',
          severity: 'danger',
        },
        accept: async () => {
          if (type === 'Aufgabe') {
            await this.aufgabenService.delete(itemId, parentId);
            this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Die Aufgabe wurde gelöscht.`, life: 3000 });
            this.createTreeNode();
          } else {
            await this.teilaufgabenService.delete(itemId, parentId);
            this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Die Teilaufgabe wurde gelöscht.`, life: 3000 });
            this.createTreeNode(parentId);
          }
        }
      });
    }
  }

  protected getColSpan(toPass: boolean) {
    if (toPass) return this.fallaufgabenService.selectedFallaufgabe.bewertung.schluessel.findIndex(s => s.note === this.fallaufgabenService.selectedFallaufgabe.bewertung.bestandenAb) + 1;
    return this.fallaufgabenService.selectedFallaufgabe.bewertung.schluessel.length - this.fallaufgabenService.selectedFallaufgabe.bewertung.schluessel.findIndex(s => s.note === this.fallaufgabenService.selectedFallaufgabe.bewertung.bestandenAb);
  }

  protected getLimitToPass() {
    return this.fallaufgabenService.selectedFallaufgabe.bewertung.bestandenAb + 0.25;
  }

  protected openEditModal(itemId: string, type: 'Aufgabe' | 'Teilaufgabe') {
    if (type === 'Aufgabe') {
      this.aufgabenService.set(itemId);
      this.isAufgabeVisible = true;
    } else {
      this.teilaufgabenService.set(itemId);
      this.isTeilaufgabeVisible = true;
    }
  }

  protected async updateFallaufgabe() {
    this.fallaufgabenService.selectedFallaufgabe.hints = this.fallaufgabenService.selectedFallaufgabe.hints.replaceAll("&nbsp;", ' ');
    await this.fallaufgabenService.update();
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Die Fallaufgabe wurde aktualisiert.`, life: 1500 });
  }

  protected async updateAufgabe() {
    // HTML-Cleaning: Tabellen-HTML bewahren, aber <p>-Tags außerhalb der Tabelle entfernen
    const cleanHtml = (html: string): string => {
      if (!html) return html;
      let cleaned = html.replaceAll("&nbsp;", ' ');

      // Wenn Tabellen vorhanden sind, nur außerhalb der Tabelle <p>-Tags entfernen
      if (cleaned.includes('quill-better-table-wrapper')) {
        const tableRegex = /<div class="quill-better-table-wrapper">[\s\S]*?<\/div>/;
        const match = cleaned.match(tableRegex);

        if (match) {
          const table = match[0];
          const beforeTable = cleaned.substring(0, match.index);
          const afterTable = cleaned.substring((match.index || 0) + table.length);

          // Hilfsfunktion für <p>-Tag-Bereinigung
          const cleanPTags = (text: string) => text.replaceAll(/<p[^>]*>/g, '').replaceAll('</p>', '<br>');

          return cleanPTags(beforeTable) + table + cleanPTags(afterTable);
        }
      }

      // Wenn keine Tabellen vorhanden: Standard-Bereinigung
      return cleaned.replaceAll(/<p[^>]*>/g, '').replaceAll('</p>', '<br>');
    };
    this.aufgabenService.selectedAufgabe.einleitung = cleanHtml(this.aufgabenService.selectedAufgabe.einleitung);
    await this.aufgabenService.update();
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Die Einleitung der Aufgabe ${this.aufgabenService.selectedAufgabe.aufgabenIndex} wurde aktualisiert.`, life: 3000 });
    this.aufgabenService.reset();
    this.createTreeNode();
  }

  protected updateBewertung() {
    const maxPoints = this.maxPointsInFallaufgabe;
    const bestGrade = 1;
    const passGrade = this.fallaufgabenService.selectedFallaufgabe.bewertung.bestandenAb;
    const worstGrade = 6;
    const halfPointStep = 0.5;
    const epsilon = 0.000001;

    const schluessel = this.fallaufgabenService.selectedFallaufgabe.bewertung.schluessel;
    const maxUnits = Math.round(maxPoints / halfPointStep);
    const passUnits = Math.round((maxPoints / 2) / halfPointStep);

    const setPointsFromUnits = (entry: { minPunkte: number }, units: number) => {
      entry.minPunkte = units * halfPointStep;
    };

    const distributeSegment = (sortedByGrade: { note: number; minPunkte: number }[], startIndex: number, endIndex: number, startUnits: number, endUnits: number) => {
      const intervals = endIndex - startIndex;
      if (intervals <= 0) {
        setPointsFromUnits(sortedByGrade[startIndex], startUnits);
        return;
      }

      const deltaUnits = endUnits - startUnits;
      for (let offset = 0; offset <= intervals; offset++) {
        const interpolatedUnits = startUnits + Math.round((offset * deltaUnits) / intervals);
        setPointsFromUnits(sortedByGrade[startIndex + offset], interpolatedUnits);
      }
    };

    schluessel.forEach((s) => {
      if (Math.abs(s.note - bestGrade) < epsilon) {
        s.minPunkte = maxPoints;
        return;
      }

      if (Math.abs(s.note - passGrade) < epsilon) {
        s.minPunkte = maxPoints / 2;
        return;
      }

      if (Math.abs(s.note - worstGrade) < epsilon) {
        s.minPunkte = 0;
        return;
      }

      const minPointsRaw = s.note < passGrade
        ? maxPoints + ((s.note - bestGrade) / (passGrade - bestGrade)) * ((maxPoints / 2) - maxPoints)
        : (maxPoints / 2) + ((s.note - passGrade) / (worstGrade - passGrade)) * (0 - (maxPoints / 2));

      const minPointsClamped = Math.max(0, Math.min(maxPoints, minPointsRaw));
      s.minPunkte = Math.round(minPointsClamped / halfPointStep) * halfPointStep;
    });

    const sortedByGrade = [...schluessel].sort((a, b) => b.note - a.note);
    const bestGradeIndex = sortedByGrade.findIndex((s) => Math.abs(s.note - bestGrade) < epsilon);
    const passGradeIndex = sortedByGrade.findIndex((s) => Math.abs(s.note - passGrade) < epsilon);
    const worstGradeIndex = sortedByGrade.findIndex((s) => Math.abs(s.note - worstGrade) < epsilon);

    if (worstGradeIndex !== -1 && passGradeIndex !== -1 && bestGradeIndex !== -1 && worstGradeIndex < passGradeIndex && passGradeIndex < bestGradeIndex) {
      distributeSegment(sortedByGrade, worstGradeIndex, passGradeIndex, 0, passUnits);
      distributeSegment(sortedByGrade, passGradeIndex, bestGradeIndex, passUnits, maxUnits);
    }
  }

  protected onContextMenu(event: any, type: 'Aufgabe' | 'Teilaufgabe', id: string) {
    this.cm.target = event.currentTarget;
    this.cm.model = this.getContextItems(type, id);
    this.cm.show(event);
  }

  protected getContextItems(type: 'Aufgabe' | 'Teilaufgabe', id: string): MenuItem[] {
    let position: number = -1;
    let maxPosition: number = -1;
    let aufgabenId: string = '';
    if (type === 'Aufgabe') {
      position = this.aufgabenService.getAufgabeById(id)?.aufgabenIndex ?? -1;
      maxPosition = this.aufgabenService.aufgaben.length - 1;
    } else {
      position = this.teilaufgabenService.getTeilAufgabeById(id)?.teilaufgabenIndex ?? -1;
      maxPosition = this.teilaufgabenService.teilaufgaben.length - 1;
      aufgabenId = this.teilaufgabenService.getTeilAufgabeById(id)?.aufgabenId ?? '';
    }
    position--;
    return [
      {
        label: 'Öffnen',
        icon: 'pi pi-search',
        command: () => { this.openEditModal(id, type); },
        visible: this.fallaufgabenService.selectedFallaufgabe.state === 'archived'
      },
      {
        label: 'Bearbeiten',
        icon: 'pi pi-pencil',
        command: () => { this.openEditModal(id, type); },
        visible: this.fallaufgabenService.selectedFallaufgabe.state !== 'archived'
      },
      {
        label: 'Teilaufgabe einfügen',
        icon: 'pi pi-paste',
        command: () => { this.pasteTeilaufgabe(id) },
        visible: type == "Aufgabe",
        disabled: !this.teilaufgabenCopyService.isTeilaufgabeinClipboard()
      },
      {
        separator: true,
      },
      {
        label: 'An erste Position schieben',
        icon: 'pi pi-angle-double-up',
        command: () => { this.moveTask(type, position, 0, aufgabenId); },
        disabled: position === 0
      },
      {
        label: 'Eine Position nach oben',
        icon: 'pi pi-angle-up',
        command: () => { this.moveTask(type, position, position - 1, aufgabenId); },
        disabled: position === 0
      },
      {
        label: 'Eine Position nach unten',
        icon: 'pi pi-angle-down',
        command: () => { this.moveTask(type, position, position + 1, aufgabenId); },
        disabled: position === maxPosition
      },
      {
        label: 'An letzte Position schieben',
        icon: 'pi pi-angle-double-down',
        command: () => { this.moveTask(type, position, maxPosition, aufgabenId); },
        disabled: position === maxPosition
      },
      {
        separator: true,
      },
      {
        label: 'Teilaufgabe Kopieren',
        icon: 'pi pi-copy',
        command: () => { this.teilaufgabenCopyService.copy(id); },
        visible: type != "Aufgabe"
      },
      {
        label: 'Löschen',
        icon: 'pi pi-trash',
        command: () => { this.confirmDelete(id, type); },
        disabled: this.fallaufgabenService.selectedFallaufgabe.state === 'archived'
      }
    ];
  }

  protected async moveTask(type: 'Aufgabe' | 'Teilaufgabe', drag: number, drop: number, aufgabenId: string = '') {
    if (type === 'Aufgabe') {
      await this.aufgabenService.reorder({
        fallaufgabenId: this.fallaufgabenService.selectedFallaufgabe._id,
        dragIndex: drag,
        dropIndex: drop
      });
      this.createTreeNode();
    } else {
      await this.teilaufgabenService.reorder({
        aufgabenId,
        dragIndex: drag,
        dropIndex: drop
      });
      this.createTreeNode(aufgabenId);
    }
  }

  protected createTableOnTemplate() {
    if (this.teilaufgabenService.selectedTeilaufgabe && this.teilaufgabenService.selectedTeilaufgabe.type === 'table') {
      this.teilaufgabenService.selectedTeilaufgabe.table = [];
      this.teilaufgabenService.selectedTeilaufgabe.table.push([{ isHeader: false, isEditable: false, value: 'A' }, { isHeader: true, isEditable: false, value: 'B' }]);
      this.teilaufgabenService.selectedTeilaufgabe.table.push([{ isHeader: false, isEditable: true, solution: 'C' }, { isHeader: false, isEditable: false, value: 'D' }]);
    }
  }
}